import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, Layers } from "lucide-react";

const empty = () => ({ name: "", description: "", phases: [{ name: "", days: 1 }] });

export default function Services() {
  const { user } = useAuth();
  const canManage = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/services"); setItems(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditingId(null); setOpen(true); };
  const openEdit = (svc) => {
    setForm({ name: svc.name, description: svc.description || "",
      phases: (svc.phases || []).map((p) => ({ name: p.name, days: p.days })) });
    setEditingId(svc.id); setOpen(true);
  };
  const addPhase = () => setForm((f) => ({ ...f, phases: [...f.phases, { name: "", days: 1 }] }));
  const removePhase = (idx) => setForm((f) => ({ ...f, phases: f.phases.filter((_, i) => i !== idx) }));
  const updPhase = (idx, patch) => setForm((f) => ({ ...f, phases: f.phases.map((p, i) => i === idx ? { ...p, ...patch } : p) }));

  const submit = async (e) => {
    e.preventDefault();
    const clean = {
      name: form.name.trim(),
      description: (form.description || "").trim(),
      phases: form.phases
        .map((p) => ({ name: (p.name || "").trim(), days: Number(p.days) || 0 }))
        .filter((p) => p.name),
    };
    if (!clean.name) return toast.error("Service name is required");
    if (clean.phases.length === 0) return toast.error("Add at least one phase");
    try {
      if (editingId) await api.patch(`/services/${editingId}`, clean);
      else await api.post("/services", clean);
      setOpen(false); load();
      toast.success(editingId ? "Service updated" : "Service created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete service "${svc.name}"? Projects already using it keep their phases.`)) return;
    try { await api.delete(`/services/${svc.id}`); load(); toast.success("Service deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Services"
        description={canManage ? "Reusable service templates with milestone phases. Attach one to a project and the phase dates auto-populate." : "Services set up by your Super Admin or Operation Head. Pick one from the dropdown when creating or editing a project."}
        actions={canManage && (
          <button data-testid="new-service-btn" onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> New Service</button>
        )}
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="services-grid">
          {items.map((s) => {
            const totalDays = (s.phases || []).reduce((a, p) => a + (p.days || 0), 0);
            return (
              <div key={s.id} data-testid={`service-card-${s.id}`} className="bg-white border border-black/5 rounded-2xl p-5 card-lift flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-2xl text-royal-800">{s.name}</div>
                    {s.description && <div className="text-sm text-ink/60 mt-1">{s.description}</div>}
                  </div>
                  {canManage && <div className="flex items-center gap-1">
                    <button data-testid={`edit-service-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 hover:bg-cream-2 rounded-md" title="Edit"><Pencil size={14} /></button>
                    <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-md" title="Delete"><Trash2 size={14} /></button>
                  </div>}
                </div>
                <div className="mt-4 label-caps flex items-center gap-2 text-royal-800"><Layers size={12} /> {s.phases.length} phase{s.phases.length === 1 ? "" : "s"} · {totalDays} days total</div>
                <ol className="mt-2 space-y-1.5 text-sm">
                  {s.phases.map((p, i) => (
                    <li key={i} className="flex justify-between border-l-2 border-royal-700/20 pl-3">
                      <span>{i + 1}. {p.name}</span>
                      <span className="font-mono-tight text-ink/60">{p.days}d</span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-full text-center py-16 text-ink/60 border border-dashed border-black/15 rounded-2xl">
              No services yet. Create one to auto-populate project phases (e.g. "Journal Publication", "Statistical Analysis").
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[1.25rem] max-w-2xl" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit Service" : "New Service"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-caps block mb-1">Name</label>
              <Input data-testid="service-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border-black/15 h-11" placeholder="e.g. Journal Publication" /></div>
            <div><label className="label-caps block mb-1">Description (optional)</label>
              <Textarea data-testid="service-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="label-caps">Milestone Phases</div>
                <button type="button" data-testid="add-phase-btn" onClick={addPhase} className="text-xs underline underline-offset-4">+ Add phase</button>
              </div>
              <div className="space-y-2">
                {form.phases.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`phase-row-${idx}`}>
                    <div className="font-mono-tight text-xs text-neutral-500 w-6">{idx + 1}.</div>
                    <Input data-testid={`phase-name-${idx}`} required value={p.name} onChange={(e) => updPhase(idx, { name: e.target.value })} className="rounded-lg border-black/15 h-10 flex-1" placeholder="Phase name (e.g. Data Collection)" />
                    <Input data-testid={`phase-days-${idx}`} required type="number" min={1} value={p.days} onChange={(e) => updPhase(idx, { days: e.target.value })} className="rounded-lg border-black/15 h-10 w-24" placeholder="days" />
                    <span className="text-xs text-neutral-500 font-mono-tight">days</span>
                    {form.phases.length > 1 && (
                      <button type="button" data-testid={`remove-phase-${idx}`} onClick={() => removePhase(idx)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <button data-testid="service-submit" type="submit" className="btn-primary">{editingId ? "Save changes" : "Create service"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
